import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ParentAPIService} from '../parent.api.service';
import {CrystalclearBackUrl} from '../config/crystalclear-back-url';
import {Token} from '../../model/security/token';
import {Login} from '../../model/security/login';
import {BehaviorSubject, Observable, Subscription, timer} from 'rxjs';
import {User} from '../../model/security/user';
import {CurrentUserInfo} from '../../model/security/current-user-info';
import {filter, map, tap} from 'rxjs/operators';
import {UserService} from '../security/user.service';
import {Locale} from '../../utils/locale';
import {SessionNames} from "../../shared/session/session-names";
import {ApiValidation} from "../../model/api/api-validation";
import {Router} from "@angular/router";
import {MessageService} from "primeng/api";
import {SecurityUserService} from "../security/security-user.service";
import {WebUserSettings} from "../../model/security/user-settings";
import {AppConfigService} from "../app-config.service";
import {LastActiveService} from "../security/LastActiveService";
import {DateTime} from "luxon";
import {CrystalClearFrontUrl} from "../../shared/pages/crystal-clear-front-url";
import {CcCookieService} from "../common/cc-cookie.service";

@Injectable({
  providedIn: 'root',
})
export class UserInfoStorageService extends ParentAPIService {
  // localStorage key tracking the set of currently open tab ids for this app, used by
  // logoutOnUnload() to detect the last-tab-closing case. Not a cookie, so kept out of
  // LocalCookieKeys (whose keys are iterated and deleted as cookies in _clearLocalState).
  private static readonly OPEN_TABS_KEY = '_CC_OPEN_TABS_';

  private tokenSubject = new BehaviorSubject<string | null>(null);

  // In-memory only (never persisted to cookies/localStorage/sessionStorage): username,
  // names, security teams and groups are PII, and a JS-writable cookie is exactly as
  // readable by an XSS payload as sessionStorage was. Backed by GET /api/users/me via
  // refreshConnectedUserCacheInfo(), which IsLoggedPageGuard already calls on every
  // guarded navigation - so this repopulates naturally after a full page reload.
  private currentUserInfo: CurrentUserInfo | null = null;

  private activityConfigurationRetrySubscription?: Subscription;
  private activityCheckSubscription?: Subscription;
  private activityCheckStarted = false;

  // Identifies this browser tab among any others open on the app, so logoutOnUnload()
  // can tell "this tab closed" apart from "the whole browser session ended" - the real
  // session cookie is shared across all tabs, so a single tab closing/reloading must
  // not tear it down for the others. See logoutOnUnload() / _registerTab().
  private readonly tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  constructor(
    private router: Router,
    protected messageService: MessageService,
    private translateService: TranslateService,
    private userService: UserService,
    private securityUserService: SecurityUserService,
    private cookiesService: CcCookieService,
    private appConfigService: AppConfigService,
    private lastActiveService: LastActiveService,
    http: HttpClient
  ) {
    super(http);

    window.onstorage = this._onStorage;
    this.tokenSubject.next(this.sessionId || null);
    this._registerTab();
  }

  /**
   * The real session id lives only in the HttpOnly cookie set by the backend and is never
   * readable from JS. This exposes the non-sensitive auth marker cookie instead - truthy
   * iff the backend currently considers the browser logged in.
   */
  public get sessionId(): string {
    return this.cookiesService.get(UserInfoStorageService.LocalCookieKeys.AUTH_MARKER);
  }

  public get lang() {
    let lang = sessionStorage.getItem(UserInfoStorageService.SessionCookieKeys.LANG);
    if (!lang) {
      lang = localStorage.getItem(UserInfoStorageService.LocalCookieKeys.LANG);
      if (!lang) {
        lang = this.translateService.getBrowserCultureLang();
      }
    }
    if (!this.translateService.langs.includes(lang)) {
      const langTmp = this.translateService.langs.find((value) => value.substring(0, value.indexOf('-')) === lang);
      if (langTmp) {
        lang = langTmp;
      } else {
        lang = this.translateService.getDefaultLang();
      }
    }

    return lang;
  }

  public set lang(lang: string) {
    this.translateService.use(lang);
    sessionStorage.setItem(UserInfoStorageService.SessionCookieKeys.LANG, lang);
  }

  public get isAuthenticated(): boolean {
    return !!this.sessionId;
  }

  public login(login: Login): Observable<ApiValidation<Token>> {
    const url = this.getUrl(CrystalclearBackUrl.LOGIN.url);
    return this.http.post<ApiValidation<Token>>(url, {
      username: login.login,
      password: login.password,
    }).pipe(
      map((apiValidation) => {
        if (apiValidation.success) {
          if (apiValidation.item.webUserSettings?.forceClearCookies) {
            this.cookiesService.deleteAll();
          }
          // The backend sets the HttpOnly session cookie plus the auth marker cookie via
          // Set-Cookie on this same response - just reflect the marker's presence here.
          this.tokenSubject.next(this.sessionId || null);
        } else {
          this.removeToken();
        }
        return apiValidation;
      })
    );
  }

  public logout() {
    // Skip the network call entirely if we already know we're not authenticated (e.g. the
    // inactivity check in handleActivityCheck() fires after a long-backgrounded tab's server
    // session has already expired naturally) - the server would just 403 an already-gone
    // session, which is harmless but shows up as a confusing failed request in the console.
    // _clearLocalState() still always runs, so the end state (logged out, redirected) is
    // identical either way.
    if (this.isAuthenticated) {
      this.delete(CrystalclearBackUrl.LOGOUT).subscribe({ error: () => {} });
    }
    this._clearLocalState();
  }

  /**
   * Sends a best-effort logout request during page unload (tab close / navigation away).
   * Uses fetch with keepalive:true so the browser keeps the request alive after the page tears down.
   * Should only be called from a beforeunload handler.
   *
   * The session cookie is shared by every tab of the browser, so this must only actually
   * log out the server session when this is the LAST open tab of the app - otherwise closing
   * or reloading one tab would silently kill the session cookie out from under every other
   * open tab (see _registerTab / _deregisterTab).
   */
  public logoutOnUnload() {
    if (this._deregisterTab() > 0) {
      return;
    }
    if (!this.isAuthenticated) {
      return;
    }
    const url = this.getUrl(CrystalclearBackUrl.LOGOUT.url);
    fetch(url, { method: 'DELETE', keepalive: true }).catch(() => {});
    this._clearLocalState();
  }

  private _registerTab(): void {
    const tabs = this._getOpenTabs();
    tabs.add(this.tabId);
    localStorage.setItem(UserInfoStorageService.OPEN_TABS_KEY, JSON.stringify([...tabs]));
  }

  /** Removes this tab from the open-tabs set and returns how many tabs remain open. */
  private _deregisterTab(): number {
    const tabs = this._getOpenTabs();
    tabs.delete(this.tabId);
    localStorage.setItem(UserInfoStorageService.OPEN_TABS_KEY, JSON.stringify([...tabs]));
    return tabs.size;
  }

  private _getOpenTabs(): Set<string> {
    try {
      const raw = localStorage.getItem(UserInfoStorageService.OPEN_TABS_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  }

  private _clearLocalState() {
    for (const cookieKey of Object.keys(UserInfoStorageService.SessionCookieKeys)) {
      sessionStorage.setItem(UserInfoStorageService.SessionCookieKeys[cookieKey], '');
    }
    for (const cookieKey of Object.keys(UserInfoStorageService.LocalCookieKeys)) {
      this.cookiesService.delete(UserInfoStorageService.LocalCookieKeys[cookieKey]);
    }
    this.tokenSubject.next(null);
    // So a subsequent login re-fetches rather than replaying this user's cached config.
    this.appConfigService.clearAuthenticatedConfig();
  }

  refreshConnectedUserCacheInfo(): Observable<CurrentUserInfo> {
    return this.get<CurrentUserInfo>(CrystalclearBackUrl.GET_CURRENT_USER).pipe(
      tap(
        (user) => {
          this.currentUserInfo = user;
          sessionStorage.setItem(SessionNames.USER_ROLES, JSON.stringify(user.roles));
        },
        (error) => {
          if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            this.removeToken();
          }
        }
      )
    );
  }

  public removeToken() {
    this.currentUserInfo = null;
    // The real session cookie is HttpOnly - it can't be deleted from JS. It carries the
    // same expiry as the server-side session and is otherwise cleared by POST /logout.
    this.cookiesService.delete(UserInfoStorageService.LocalCookieKeys.AUTH_MARKER);
    localStorage.removeItem(UserInfoStorageService.LocalCookieKeys.LANG);
    sessionStorage.removeItem(SessionNames.USER_ROLES);
    this.tokenSubject.next(null);
  }

  public setLangFromStorage() {
    for (const key of Object.keys(Locale.LANGS)) {
      if (localStorage.getItem(UserInfoStorageService.LocalCookieKeys.LANG) === Locale.LANGS[key].name) {
        this.lang = Locale.LANGS[key].id;
      }
    }
  }

  public getLangFromStorage() {
    return localStorage.getItem(UserInfoStorageService.LocalCookieKeys.LANG);
  }

  /**
   * @deprecated kept for compatibility with existing call sites; now backed by the
   * in-memory cache from GET /api/users/me instead of a JS-readable cookie.
   */
  public getUsernameFromStorage() {
    return this.currentUserInfo?.username || null;
  }

  public getFirstName(): string | null {
    return this.currentUserInfo?.firstName || null;
  }

  public getLastName(): string | null {
    return this.currentUserInfo?.lastName || null;
  }

  public getSecurityTeams(): string[] {
    return this.currentUserInfo?.securityTeams || [];
  }

  public getUserGroups(): string[] {
    return this.currentUserInfo?.userGroupNames || [];
  }

  private _onStorage: (storageEvent: StorageEvent) => any = (ev) => {
    if (
      Object.keys(UserInfoStorageService.LocalCookieKeys)
        .map((value) => UserInfoStorageService.LocalCookieKeys[value])
        .includes(ev.key)
    ) {
      sessionStorage.setItem(ev.key, ev.newValue);
    }
  };

  protected infoMessage(message: string) {
    if (message) {
      this.messageService.add({
        severity: 'info',
        icon: 'fas fa-info-circle',
        detail: this.translateService.instant(message),
      });
    }
  }

  public warnMessage(message: string) {
    if (message) {
      console.log('Warning: ' + message);
      this.messageService.add({
        severity: 'warn',
        icon: 'fas fa-triangle-exclamation',
        detail: this.translateService.instant(message),
        life: 10000
      });
    }
  }

  protected errorMessage(message: string) {
    if (message) {
      this.messageService.add({
        severity: 'error',
        icon: 'fas fa-bomb',
        detail: this.translateService.instant(message),
        // Keep on screen until dismissed so a support reference code can be read/copied.
        sticky: true,
        closable: true
      });
    }
  }

  public setUp() {
    this.waitForActivityConfiguration();
  }

  private waitForActivityConfiguration(): void {
    if (this.activityCheckStarted) {
      return;
    }

    const checkInterval = this.appConfigService.noActivityCheckInterval;

    if (!checkInterval) {
      this.activityConfigurationRetrySubscription?.unsubscribe();
      this.activityConfigurationRetrySubscription = timer(250)
        .subscribe(() => this.waitForActivityConfiguration());
      return;
    }

    this.startActivityCheck(checkInterval);
  }

  private startActivityCheck(checkInterval: number): void {
    if (this.activityCheckStarted) {
      return;
    }

    this.activityCheckStarted = true;
    this.activityConfigurationRetrySubscription?.unsubscribe();
    this.activityCheckSubscription?.unsubscribe();

    this.activityCheckSubscription = timer(0, checkInterval)
      .pipe(filter(() => this.isAuthenticated))
      .subscribe(() => this.handleActivityCheck());
  }

  private handleActivityCheck(): void {
    const currentDate = DateTime.now();
    const lastActiveDate = this.lastActiveService.getLastActiveDate();

    if (currentDate.diff(lastActiveDate).milliseconds > this.appConfigService.noActivityTimeout) {
      this.logout();
      this.router.navigate(['/' + CrystalClearFrontUrl.LOGIN.path]);
      return;
    }

    this.refreshActivityIfNeeded(lastActiveDate);
  }

  private refreshActivityIfNeeded(lastActiveDate: DateTime): void {
    if (!this.appConfigService.saveLastActivity) {
      return;
    }

    const username = this.currentUserInfo?.username;
    if (!username) {
      return;
    }

    const user = new User();
    user.username = username;
    user.webUserSettings = new WebUserSettings();

    if (this.appConfigService.cookiesSaveEnabled) {
      user.webUserSettings.cookies = JSON.stringify(this.cookiesService.getAll());
    }

    user.webUserSettings.lastActivityDate = lastActiveDate;

    const refreshActivityObservable = this.securityUserService.refreshActivity(user);
    if (refreshActivityObservable) {
      refreshActivityObservable.subscribe();
    }
  }

  public getTokenObservable(): Observable<string | null> {
    return this.tokenSubject.asObservable();
  }
}

export module UserInfoStorageService {
  export class LocalCookieKeys {
    // Non-sensitive marker set by the backend alongside the real HttpOnly session cookie
    // (which is never readable from JS - see SessionTokenFilter.SESSION_COOKIE_NAME server-side).
    // Username/names/security teams/groups are NOT stored here anymore - they're PII, and a
    // JS-writable cookie is exactly as readable by an XSS payload as sessionStorage was. They
    // now live only in the in-memory currentUserInfo cache above (see getUsernameFromStorage()
    // and friends), backed by GET /api/users/me.
    public static readonly AUTH_MARKER = '_CC_AUTHENTICATED_';
    public static readonly LANG = '_LANG_';
  }

  export class SessionCookieKeys {
    public static readonly LANG = '_LANG_';
    public static readonly ROLE_LABEL = '_ROLE_LABEL_';
  }
}
